/**
 * Deutsche Feiertage (Datenanreicherung Welle A) — rein deterministisch, kein Netzwerk-Call.
 *
 * Deutsche Feiertage sind vollstaendig berechenbar (Gauss'sche/Meeus-Osterformel + feste
 * Offsets + Bundesland-Regeln) — robuster als eine Abhaengigkeit von einer Dritt-API fuer ein
 * so grundlegendes Kalender-Feature. Wird direkt vom Frontend importiert (apps/web/src/pages/
 * calendar.tsx), kein Backend-Roundtrip.
 *
 * WICHTIG (akzeptierte Einschraenkung, kein Bug): Fronleichnam und Mariae Himmelfahrt sind in
 * der Realitaet teils nur GEMEINDE-Ebene (mehrheitlich katholische Gemeinden in z.B. Sachsen/
 * Thueringen bzw. in bayerischen Gemeinden bei Mariae Himmelfahrt), nicht landesweit. Diese
 * Funktion rechnet bewusst nur auf Bundesland-Ebene — eine feingranulare Gemeinde-Datenbank
 * waere fuer den Kalender-Anwendungsfall (Anzeige im Betriebs-Kalender) unverhaeltnismaessig.
 */

export interface Holiday {
  /** ISO-Datum (YYYY-MM-DD). */
  date: string;
  name: string;
  /** true = gilt bundesweit; false = nur im uebergebenen Bundesland. */
  nationwide: boolean;
}

export interface Bundesland {
  code: string;
  name: string;
}

/** Die 16 deutschen Bundeslaender (ISO 3166-2:DE-Kuerzel) + Anzeigename. */
export const BUNDESLAENDER: Bundesland[] = [
  { code: "BW", name: "Baden-Württemberg" },
  { code: "BY", name: "Bayern" },
  { code: "BE", name: "Berlin" },
  { code: "BB", name: "Brandenburg" },
  { code: "HB", name: "Bremen" },
  { code: "HH", name: "Hamburg" },
  { code: "HE", name: "Hessen" },
  { code: "MV", name: "Mecklenburg-Vorpommern" },
  { code: "NI", name: "Niedersachsen" },
  { code: "NW", name: "Nordrhein-Westfalen" },
  { code: "RP", name: "Rheinland-Pfalz" },
  { code: "SL", name: "Saarland" },
  { code: "SN", name: "Sachsen" },
  { code: "ST", name: "Sachsen-Anhalt" },
  { code: "SH", name: "Schleswig-Holstein" },
  { code: "TH", name: "Thüringen" },
];

const BUNDESLAND_CODES = new Set(BUNDESLAENDER.map((b) => b.code));

/**
 * Ostersonntag (gregorianisch) nach der Gauss'schen Osterformel (Meeus/Jones/Butcher-Variante).
 * UTC-Datum, damit Zeitzonenverschiebungen keine Tagesabweichung verursachen koennen.
 */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = Maerz, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Buss- und Bettag: Mittwoch VOR dem 23. November (nicht: der 23. selbst, falls der zufaellig
 * ein Mittwoch ist — dann gilt die Vorwoche). Nur noch gesetzlicher Feiertag in Sachsen (seit
 * der bundesweiten Abschaffung 1995 zur Gegenfinanzierung der Pflegeversicherung).
 */
export function bussUndBettag(year: number): Date {
  const nov23 = new Date(Date.UTC(year, 10, 23));
  const dow = nov23.getUTCDay(); // 0=So .. 3=Mi .. 6=Sa
  let diff = (dow - 3 + 7) % 7;
  if (diff === 0) diff = 7; // faellt der 23. selbst auf Mittwoch, gilt die Vorwoche
  return addDays(nov23, -diff);
}

/**
 * Feiertage eines Jahres fuer ein Bundesland (bundesweite + regionale), aufsteigend sortiert.
 * `bundesland` ist eines der 16 Kuerzel aus BUNDESLAENDER (case-insensitiv); unbekannte/leere
 * Werte liefern nur die bundesweiten Feiertage (kein Fehler, keine Annahme).
 */
export function germanHolidays(year: number, bundesland: string): Holiday[] {
  const code = (bundesland || "").toUpperCase();
  const known = BUNDESLAND_CODES.has(code) ? code : null;
  const easter = easterSunday(year);

  const list: Holiday[] = [];
  const add = (date: string, name: string, nationwide: boolean, states?: string[]) => {
    if (nationwide || (known && states && states.includes(known))) {
      list.push({ date, name, nationwide });
    }
  };
  const fixed = (month: number, day: number) => iso(new Date(Date.UTC(year, month - 1, day)));
  const relEaster = (offsetDays: number) => iso(addDays(easter, offsetDays));

  // --- Bundesweite Feiertage (9) ---------------------------------------------
  add(fixed(1, 1), "Neujahr", true);
  add(relEaster(-2), "Karfreitag", true);
  add(relEaster(1), "Ostermontag", true);
  add(fixed(5, 1), "Tag der Arbeit", true);
  add(relEaster(39), "Christi Himmelfahrt", true);
  add(relEaster(50), "Pfingstmontag", true);
  add(fixed(10, 3), "Tag der Deutschen Einheit", true);
  add(fixed(12, 25), "1. Weihnachtsfeiertag", true);
  add(fixed(12, 26), "2. Weihnachtsfeiertag", true);

  // --- Regionale Feiertage ----------------------------------------------------
  add(fixed(1, 6), "Heilige Drei Könige", false, ["BW", "BY", "ST"]);
  add(fixed(3, 8), "Internationaler Frauentag", false, ["BE", "MV"]);
  // Landesweiter Feiertag nur in diesen 6 Laendern (s. Modul-Kommentar oben).
  add(relEaster(60), "Fronleichnam", false, ["BW", "BY", "HE", "NW", "RP", "SL"]);
  // Landesweit nur im Saarland; in Bayern nur gemeinde-/konfessionsabhaengig (s. o.).
  add(fixed(8, 15), "Mariä Himmelfahrt", false, ["SL", "BY"]);
  // Tatsaechlich nur in der Stadt Augsburg gesetzlicher Feiertag, nicht landesweit in Bayern —
  // gleiche akzeptierte Bundesland-Ebene-Vereinfachung wie bei Fronleichnam/Mariae Himmelfahrt.
  add(fixed(8, 8), "Augsburger Friedensfest", false, ["BY"]);
  // Berlin und Bayern gehoeren NICHT dazu (haeufiger Irrtum) — seit 2018 auch HB/HH/NI/SH.
  add(fixed(10, 31), "Reformationstag", false, ["BB", "HB", "HH", "MV", "NI", "SH", "SN", "ST", "TH"]);
  add(fixed(11, 1), "Allerheiligen", false, ["BW", "BY", "NW", "RP", "SL"]);
  add(iso(bussUndBettag(year)), "Buß- und Bettag", false, ["SN"]);
  // M-N (Bughunt 2026-07-11): Weltkindertag - gesetzlicher Feiertag nur in Thueringen (seit 2019).
  add(fixed(9, 20), "Weltkindertag", false, ["TH"]);

  return list.sort((x, y) => x.date.localeCompare(y.date));
}
